import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	attachInvestorsToClassifier,
	createMipClassifier,
	detachInvestorsFromClassifier,
	listClassifiersForInvestor,
	listMipClassifiers,
	updateMipClassifier,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/services/mipClassifiers.service';
import {
	CreateMipClassifierDto,
	InvestorAssignmentDto,
	UpdateMipClassifierDto,
} from '@dealstack/shared/lib/generated';

const QUERY_KEY = 'mipClassifiers';
const INVESTOR_CLASSIFIERS_QUERY_KEY = 'mipClassifiersByInvestor';

export const useMipClassifiersList = ({
	mipId,
	isArchived,
	classifiedType,
	enabled = true,
}: {
	mipId: string;
	isArchived?: boolean;
	classifiedType?: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: [QUERY_KEY, mipId, { isArchived, classifiedType }],
		queryFn: () => listMipClassifiers({ isArchived, classifiedType }),
		select: (res) => res.payload ?? [],
		enabled,
	});
};

const errorMessage = (e: unknown, fallback: string) => {
	const detail = (e as { error?: { message?: string } } | undefined)?.error?.message;
	return detail ?? fallback;
};

export const useCreateMipClassifier = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: CreateMipClassifierDto) => createMipClassifier(body),
		onSuccess: () => {
			showToastV2({ state: 'success', message: 'Classifier created' });
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to create classifier'),
			});
		},
	});
};

export const useUpdateMipClassifier = ({ mipId }: { mipId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { mipClassifierId: string; body: UpdateMipClassifierDto }) =>
			updateMipClassifier(args),
		onSuccess: () => {
			showToastV2({ state: 'success', message: 'Classifier updated' });
			return queryClient.invalidateQueries({ queryKey: [QUERY_KEY, mipId] });
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to update classifier'),
			});
		},
	});
};

export const useClassifiersForInvestor = ({ investorId }: { investorId: string }) => {
	return useQuery({
		queryKey: [INVESTOR_CLASSIFIERS_QUERY_KEY, investorId],
		queryFn: () => listClassifiersForInvestor(investorId),
		select: (res) => res.payload ?? [],
		enabled: !!investorId,
	});
};

export const useAttachInvestorToClassifier = ({ investorId }: { investorId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { mipClassifierId: string; body: InvestorAssignmentDto }) =>
			attachInvestorsToClassifier(args),
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [INVESTOR_CLASSIFIERS_QUERY_KEY, investorId],
			});
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to add investor to classifier'),
			});
		},
	});
};

export const useDetachInvestorFromClassifier = ({ investorId }: { investorId: string }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { mipClassifierId: string; body: InvestorAssignmentDto }) =>
			detachInvestorsFromClassifier(args),
		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [INVESTOR_CLASSIFIERS_QUERY_KEY, investorId],
			});
		},
		onError: (e) => {
			showToastV2({
				state: 'error',
				message: errorMessage(e, 'Failed to remove investor from classifier'),
			});
		},
	});
};
